import { Injectable, inject } from '@angular/core';
import { DbService } from '../db/db.service';
import { Project } from '../../models/project';
import { CategoryEnrollment } from '../../models/categoryEnrollment';
import { User } from '../../models/user';
import { CourseEnrollment } from '../../models/courseEnrollment';
import { BehaviorSubject, Observable } from 'rxjs';
import { Timestamp } from "firebase/firestore";
import { Favorite } from '../../models/favorite';
import { AssignedCategory } from '../../models/assignedCategory';
import { Category } from '../../models/category';
import { ApiService } from '../api/api.service';
import { AssignedCategoryService } from '../assigned-category/assigned-category.service';
import { CategoryService } from '../category/category.service';
import {
	MyCoursesSummary,
	isCurrentSummary,
	mapSummaryToCategoryEnrollments,
	readStoredSummary,
	writeStoredSummary
} from '../my-courses-summary/my-courses-summary.mapper';

/** Where the current My Courses list came from; reported to Sentry with the load timings. */
export type MyCoursesSource = 'cache' | 'summary' | 'legacy';

/** Milliseconds since `listenCategoryEnrollment` was called, per step, so a slow report says which step was slow. */
export interface MyCoursesLoadTimings {
	[step: string]: number;
}

export interface MyCoursesState {
	categoriesEnrollments: CategoryEnrollment[];
	source: MyCoursesSource;
	timings: MyCoursesLoadTimings;
}

@Injectable({
	providedIn: 'root'
})
export class CategoryEnrollmentService {

	/**
	 * The My Courses list. `null` until something has been loaded for the current learner; after
	 * that it keeps the last value across navigation so coming back to the page paints instantly.
	 */
	private myCourses = new BehaviorSubject<MyCoursesState | null>(null);
	myCourses$: Observable<MyCoursesState | null> = this.myCourses.asObservable();

	private summaryUnsubscribe: (() => void) | null = null;
	private legacyUnsubscribe: (() => void) | null = null;
	private listeningFor: string | null = null;
	private rebuildRequested = false;
	private loadStartedAt = 0;

	// Legacy-path state: per-enrollment cache and in-order snapshot processing.
	private enrollmentsById = new Map<string, CategoryEnrollment>();
	private snapshotQueue: Promise<void> = Promise.resolve();

	private db = inject(DbService);
	private apiService = inject(ApiService);
	private assignedCategoryService = inject(AssignedCategoryService);
	private categoryService = inject(CategoryService);

	private static readonly EXCLUDED_FROM_GENERIC_SAVE = [
		'attestation_signed_at',
		'attestation_signature_url',
		// Owned by the auto-grant-retakes endpoint. The model carries it only to be read, and an
		// enrollment that has never been set through that endpoint has no value at all here — writing
		// the model's null back would erase a per-learner override.
		'auto_grant_retakes',
	];

	async save(model: CategoryEnrollment, userId: string, projectId: string) {
		const json = model.toJson();
		for (const field of CategoryEnrollmentService.EXCLUDED_FROM_GENERIC_SAVE) {
			delete json[field];
		}
		await this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(CategoryEnrollment.PATH)
			.doc(model.id)
			.set(json, { merge: true });
	}

	async getById(userId: string, categoryEnrollmentId: string, projectId: string): Promise<CategoryEnrollment | null> {
		const snapshot = await this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(CategoryEnrollment.PATH)
			.doc(categoryEnrollmentId)
			.get();
		if (snapshot.exists) {
			return new CategoryEnrollment(snapshot.data());
		}
		return null;
	}

	listenById(
		userId: string,
		categoryEnrollmentId: string,
		projectId: string,
		next: (categoryEnrollment: CategoryEnrollment | null) => void,
		error?: (error: unknown) => void
	): () => void {
		return this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(CategoryEnrollment.PATH)
			.doc(categoryEnrollmentId)
			.onSnapshot(
				snapshot => next(snapshot.exists ? new CategoryEnrollment(snapshot.data()) : null),
				error
			);
	}

	async getByCategory(userId: string, categoryId: string, projectId: string): Promise<CategoryEnrollment | null> {
		const snapshot = await this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(CategoryEnrollment.PATH)
			.where('category_id', '==', categoryId)
			.get();
		if (snapshot.docs[0].exists) {
			return new CategoryEnrollment(snapshot.docs[0].data());
		}
		return null;
	}


	async getAllByUser(user: User, projectId: string) : Promise<CategoryEnrollment[]> {
		const categoryEnrollmentPath = this.db.getProyIdRef(projectId).collection(User.PATH).doc(user.id).collection(CategoryEnrollment.PATH)

		const snapshot = await categoryEnrollmentPath.where('is_deleted', '==', false).get();

		if (snapshot.empty) {
			return null
		}

		const categoriesEnrollment = Promise.all(snapshot.docs.map(async doc => {
			const categoryEnrollment = new CategoryEnrollment(doc.data())
			const snapshotCourseEnrollment = await categoryEnrollmentPath.doc(categoryEnrollment.id).collection(CourseEnrollment.PATH)
				.where('is_deleted', '==', false)
				.get();
			categoryEnrollment.__coursesEnrollments = snapshotCourseEnrollment.docs.map(course => new CourseEnrollment(course.data()));
			return categoryEnrollment;
		}));
		return categoriesEnrollment;
	}

	async changeLanguage(userId: string, projectId: string, languageCode: string): Promise<void> {
		await this.apiService.post('/categoryEnrollment/change-enrollments-language', { userId, languageCode }, { showAPIError: false });
	}

	async updateCurrentLanguage(categoryEnrollment: CategoryEnrollment, userId: string, projectId: string) {
		const snapshot = await this.db.getProyIdRef(projectId).collection(User.PATH).doc(userId).collection(CategoryEnrollment.PATH)
			.doc(categoryEnrollment.id).update({ ['selected_language']: categoryEnrollment.selectedLanguage });
	}

	async deleteExpired(projectId: string, categoryEnrollment: CategoryEnrollment, userId: string) {
		const snapshot = await this.db.getProyIdRef(projectId).collection(User.PATH).doc(userId).collection(CategoryEnrollment.PATH)
			.doc(categoryEnrollment.id).update({ ['is_deleted']: true })
	}

	async deleteExpiredFavoriteContent(projectId: string, userId: string, categoryEnrollment: CategoryEnrollment) {
		const favoriteRef = this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(Favorite.PATH);

		const snapshot = await favoriteRef
			.where('category_id', '==', categoryEnrollment.id)
			.get();

		if (!snapshot.empty) {
			const batch = this.db.firestore.batch();
			snapshot.docs.forEach(doc => {
				batch.update(doc.ref, { is_deleted: true });
			});
			await batch.commit();
		}
	}

	/**
	 * Streams the learner's My Courses list.
	 *
	 * The normal path is one document: the server-maintained `summaries/myCourses` read model,
	 * which already carries every category and course the page renders plus the category flag and
	 * the client's orientation-form settings. One read, one small parse — no per-category fan-out
	 * and none of the chapter/quiz history that lives on the course enrollment documents.
	 *
	 * Before the network answers, the last summary this learner saw (kept in localStorage) is
	 * emitted so a returning learner never waits on a skeleton.
	 *
	 * If the summary doesn't exist yet (a learner enrolled before it was introduced) the page falls
	 * back to the original per-enrollment reads and asks the server to build the summary, so the
	 * next visit takes the fast path. The summary listener stays open and takes over the moment
	 * the document appears.
	 */
	listenCategoryEnrollment(projectId: string, userId: string, clientUsername?: string) {
		const key = `${projectId}/${userId}`;
		this.unsubscribed();
		if (this.listeningFor !== key) {
			this.listeningFor = key;
			this.rebuildRequested = false;
			this.enrollmentsById.clear();
			this.myCourses.next(null);
		}
		this.loadStartedAt = performance.now();

		// Re-emit what is already in memory as a cache hit, so the page treats it as an instant
		// paint and still waits for the live snapshot before reporting load time.
		const remembered = this.myCourses.value;
		if (remembered) {
			this.emit(remembered.categoriesEnrollments, 'cache', { memoryMs: 0 });
		} else {
			this.hydrateFromStorage(projectId, userId);
		}

		this.summaryUnsubscribe = this.db.firestore
			.collection(Project.PATH).doc(projectId)
			.collection(User.PATH).doc(userId)
			.collection(MyCoursesSummary.PATH).doc(MyCoursesSummary.DOC_ID)
			.onSnapshot(
				(snapshot) => {
					const snapshotMs = this.elapsed();
					const data = snapshot.exists ? snapshot.data() : null;
					if (!isCurrentSummary(data)) {
						this.startLegacyListener(projectId, userId, clientUsername);
						this.requestSummaryRebuild();
						return;
					}

					this.stopLegacyListener();
					const mapStartedAt = performance.now();
					const categoriesEnrollments = mapSummaryToCategoryEnrollments(data);
					this.emit(categoriesEnrollments, 'summary', {
						summarySnapshotMs: snapshotMs,
						mapMs: Math.round(performance.now() - mapStartedAt),
						totalMs: this.elapsed()
					});
					writeStoredSummary(projectId, userId, data);
				},
				(error) => {
					console.error('Error listening to My Courses summary:', error);
					this.startLegacyListener(projectId, userId, clientUsername);
				}
			);
	}

	private hydrateFromStorage(projectId: string, userId: string): void {
		const stored = readStoredSummary(projectId, userId, this.db.firestore);
		if (!stored) {
			return;
		}
		this.emit(mapSummaryToCategoryEnrollments(stored), 'cache', { cacheMs: this.elapsed() });
	}

	private requestSummaryRebuild(): void {
		if (this.rebuildRequested) {
			return;
		}
		this.rebuildRequested = true;
		this.apiService.post('/myCoursesSummary/rebuild', {}, { showAPIError: false })
			.catch(error => console.error('Could not request My Courses summary rebuild:', error));
	}

	private emit(categoriesEnrollments: CategoryEnrollment[], source: MyCoursesSource, timings: MyCoursesLoadTimings): void {
		this.myCourses.next({ categoriesEnrollments, source, timings });
	}

	private elapsed(): number {
		return Math.round(performance.now() - this.loadStartedAt);
	}

	// -----------------------------------------------------------------------------------------
	// Legacy path: per-enrollment reads. Only used while a learner has no summary document.
	// -----------------------------------------------------------------------------------------

	private startLegacyListener(projectId: string, userId: string, clientUsername?: string): void {
		if (this.legacyUnsubscribe) {
			return;
		}
		if (clientUsername) {
			// Warm the client lookup while the enrollment query is in flight so the
			// assigned-category reads below don't wait on it.
			this.assignedCategoryService.getClientUserRef(clientUsername, projectId).catch((): null => null);
		}
		this.legacyUnsubscribe = this.db.firestore
			.collection(Project.PATH).doc(projectId)
			.collection(User.PATH).doc(userId)
			.collection(CategoryEnrollment.PATH)
			.where('is_deleted', '==', false)
			.where('is_hidden', '==', false)
			.onSnapshot(
				(snapshot) => {
					// Snapshots must be processed strictly in arrival order: the course
					// fetches inside are async, and letting a slow older snapshot finish
					// after a newer one would emit stale data over fresh data.
					const queryMs = this.elapsed();
					this.snapshotQueue = this.snapshotQueue.then(() => this.processLegacySnapshot(snapshot, projectId, userId, clientUsername, queryMs));
				},
				error => {
					console.error("Error config subscription:", error);
					// Emit so subscribers waiting on a first live emission can settle
					// on the empty state instead of loading forever.
					this.emit([], 'legacy', { errorMs: this.elapsed() });
				}
			);
	}

	private stopLegacyListener(): void {
		if (this.legacyUnsubscribe) {
			this.legacyUnsubscribe();
			this.legacyUnsubscribe = null;
		}
	}

	private async processLegacySnapshot(snapshot: any, projectId: string, userId: string, clientUsername: string | undefined, queryMs: number): Promise<void> {
		// The summary took over while this snapshot was queued.
		if (!this.legacyUnsubscribe) {
			return;
		}
		try {
			const fanOutStartedAt = performance.now();
			const changedIds = new Set<string>();
			for (const change of snapshot.docChanges()) {
				if (change.type === 'removed') {
					this.enrollmentsById.delete(change.doc.id);
				} else {
					changedIds.add(change.doc.id);
				}
			}

			// The full list is always rebuilt from snapshot.docs so no enrollment can
			// be lost, but courses are only re-fetched for docs that actually changed —
			// a language switch touches every enrollment doc and would otherwise
			// trigger N re-fetches per emitted snapshot.
			const categoriesEnrollments = await Promise.all(snapshot.docs.map(async (doc: any) => {
				const cached = this.enrollmentsById.get(doc.id);
				if (cached && !changedIds.has(doc.id)) {
					return cached;
				}

				const categoryEnrollment = new CategoryEnrollment({
					...doc.data(),
					id: doc.id
				});

				if (categoryEnrollment.languages.length === 1 || categoryEnrollment.selectedLanguage) {
					categoryEnrollment.translated = true;
				}

				// All three reads are independent, so they share one round trip. Each
				// one degrades on its own: a failure must not hide the enrollment or
				// discard the rest of the snapshot, so fall back to the last known value.
				const [courses, assignedCategory, category] = await Promise.all([
					this.db.firestore
						.collection(Project.PATH).doc(projectId)
						.collection(User.PATH).doc(userId)
						.collection(CategoryEnrollment.PATH)
						.doc(categoryEnrollment.id)
						.collection(CourseEnrollment.PATH)
						.where('is_deleted', '==', false)
						.get()
						.then(courseSnapshot => courseSnapshot.docs.map(courseDoc => new CourseEnrollment(courseDoc.data())))
						.catch((error): CourseEnrollment[] => {
							console.error(`Error loading courses for category enrollment ${categoryEnrollment.id}:`, error);
							return cached?.__coursesEnrollments ?? [];
						}),
					clientUsername
						? this.assignedCategoryService.getAssignedCategoryByClient(clientUsername, categoryEnrollment.id, projectId)
							.catch((error): AssignedCategory | null => {
								console.error(`Error loading assigned category for ${categoryEnrollment.id}:`, error);
								return cached?.__assignedCategory ?? null;
							})
						: Promise.resolve(cached?.__assignedCategory ?? null),
					this.categoryService.getById(categoryEnrollment.id, projectId)
						.catch((error): Category | null => {
							console.error(`Error loading category ${categoryEnrollment.id}:`, error);
							return cached?.__category ?? null;
						})
				]);

				categoryEnrollment.__coursesEnrollments = courses;
				categoryEnrollment.__assignedCategory = assignedCategory;
				categoryEnrollment.__category = category;

				this.enrollmentsById.set(doc.id, categoryEnrollment);
				return categoryEnrollment;
			}));

			if (!this.legacyUnsubscribe) {
				return;
			}
			this.emit(categoriesEnrollments, 'legacy', {
				enrollmentQueryMs: queryMs,
				fanOutMs: Math.round(performance.now() - fanOutStartedAt),
				totalMs: this.elapsed()
			});
		} catch (error) {
			console.error("Error snapshot:", error);
		}
	}

	async unsubscribed() {
		if (this.summaryUnsubscribe) {
			this.summaryUnsubscribe();
			this.summaryUnsubscribe = null;
		}
		this.stopLegacyListener();
	}

	async getCategoryWithEnrollmentsById(userId: string, categoryId: string, projectId: string): Promise<CategoryEnrollment | null> {
		const categoryRef = this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(CategoryEnrollment.PATH)
			.doc(categoryId);

		const snapshot = await categoryRef.get()

		if (!snapshot.exists) {
			return null;
		}
		const categoryEnrollment = new CategoryEnrollment(snapshot.data());
		const courseSnapshot = await categoryRef
			.collection(CourseEnrollment.PATH)
			.where('is_deleted', '==', false)
			.get();

		categoryEnrollment.__coursesEnrollments = courseSnapshot?.docs.map(courseDoc => new CourseEnrollment(courseDoc.data()));

		return categoryEnrollment;
	}

	async updateCompletedPercentage(projectId: string, categoryEnrollment: CategoryEnrollment, userId: string, assignedCategory: AssignedCategory) {
		const updateData: any = {
			completed_percentage: categoryEnrollment.completedPercentage,
		};
		if (assignedCategory?.formLink && categoryEnrollment?.completedForm && categoryEnrollment?.completedPercentage === 100) {
			updateData.completed_at = Timestamp.now();
		}
		if (!assignedCategory?.formLink && categoryEnrollment?.completedPercentage === 100) {
			updateData.completed_at = Timestamp.now();
		}
		if (updateData?.completed_at) {
			categoryEnrollment.completedPercentage = 100;
		}
		await this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(CategoryEnrollment.PATH)
			.doc(categoryEnrollment.id)
			.update(updateData);
	}

	async saveAttestation(categoryEnrollment: CategoryEnrollment, userId: string, projectId: string): Promise<void> {
		const updateData: { [key: string]: any } = {
			attestation_signed_at: categoryEnrollment.attestationSignedAt,
		};
		if (categoryEnrollment?.attestationSignatureUrl) {
			updateData['attestation_signature_url'] = categoryEnrollment.attestationSignatureUrl;
		}
		await this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(User.PATH)
			.doc(userId)
			.collection(CategoryEnrollment.PATH)
			.doc(categoryEnrollment.id)
			.update(updateData);
	}

	/** Forgets everything loaded for the current learner. */
	clearCategoriesEnrollments() {
		this.unsubscribed();
		this.listeningFor = null;
		this.rebuildRequested = false;
		this.enrollmentsById.clear();
		this.myCourses.next(null);
	}


}
